<svelte:options runes={false} />

<script lang="ts">
	// Formatting
	import { format } from 'svelte-i18n';
	// Design system components
	import { Alert, ControlsContainer, Select, Text } from '@Shelly/shelly-design-system';
	// Stores
	import { selectraPrices } from 'src/stores/selectra';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { getUserTimeZoneDate } from 'src/helpers/date_helper';
	import {
		formatSelectraPeriodDate,
		getSelectraRangeOptions,
	} from 'src/helpers/selectra_tariff_helpers';
	// Config
	import { getSelectraTariffChartConfig } from 'src/helpers/statistics/config/tariff_intervals';
	// Components
	import LayerChartRenderer from 'src/components/Chart/LayerChart/LayerChartRenderer.svelte';
	// Types
	import type { ChartDataInterface, TimeZoneInterface } from 'src/types/Chart';

	let rangeValue: { title: string; value: number } | undefined;
	let chartData: ChartDataInterface | undefined;
	let userTimeZone: TimeZoneInterface = getUserTimeZoneDate();

	$: rangeOptions = getSelectraRangeOptions($selectraPrices?.prices ?? []);

	$: if (rangeOptions.length && !rangeOptions.find((option) => option.value === rangeValue?.value)) {
		rangeValue = rangeOptions[0];
	}

	$: chartData = rangeValue
		? getSelectraTariffChartConfig($selectraPrices, rangeValue.value)
		: undefined;

	const onChangeRange = (range: number) => {
		rangeValue = rangeOptions.find((option) => option.value === range);
	};

	const selectChangeRange = (e: CustomEvent | { detail: { value: number } }) => {
		onChangeRange(e.detail.value);
	};
</script>

<Text styleType="controls-large" class="mb-m text-bold">
	{$format('pricing_overview_heading', { default: 'Pricing Overview' })}
</Text>

{#if !$selectraPrices?.prices?.length}
	<Alert
		type="info"
		heading={$format('label_no_selectra_prices', {
			default: 'No price data is available yet.',
		})}
	/>
{:else if rangeValue && chartData}
	<ControlsContainer class="mb-s" color={$showNewLayout ? 'color-1' : 'color-2'}>
		<svelte:fragment slot="first">
			<div class="selectra-tariff-chart">
				<div class="width-full mb-s">
					<Select
						id="select-selectra-tariff-chart-range"
						aria-label={$format('select_tariff_chart_range', {
							default: 'Select tariff chart range',
						})}
						value={rangeValue}
						size="m"
						width="full"
						icon="fas fa-clock-rotate-left"
						options={rangeOptions}
						on:change={selectChangeRange}
					/>
				</div>
				{#key `${rangeValue.value}-${$selectraPrices?.prices?.length}-${$selectraPrices?.next_update}`}
					<LayerChartRenderer {chartData} />
				{/key}
				{#if userTimeZone}
					<Text size="xs" class="mt-s tiny-styles text-secondary">
						{capFirstChar(
							$format('local_time_zone_user_account', {
								default: 'local time-zone of the user account',
							}),
						)}: {userTimeZone.tz}
						{#if userTimeZone.timeZoneOffset != 0}
							{` (${userTimeZone.timeZoneOffset > 0 ? '+' : ''}${userTimeZone.timeZoneOffset}${$format(
								'local_time_zone_from_current_time_zone',
								{
									default: 'h from current time zone',
								},
							)})`}
						{/if}
					</Text>
				{/if}
				{#if $selectraPrices.next_update}
					<Text size="xs" class="mt-s tiny-styles text-secondary">
						{$format('label_selectra_next_update', {
							default: 'Next update: {time}',
							values: { time: formatSelectraPeriodDate($selectraPrices.next_update) },
						})}
					</Text>
				{/if}
			</div>
		</svelte:fragment>
	</ControlsContainer>
{/if}

<style>
	.selectra-tariff-chart :global(.layer-chart-theme),
	.selectra-tariff-chart :global(.layer-chart-theme .lc-area-chart),
	.selectra-tariff-chart :global(.layer-chart-theme svg) {
		overflow: visible;
	}

	.selectra-tariff-chart :global(.layer-chart-theme .lc-area-chart) {
		padding-bottom: var(--spacing-m);
		box-sizing: border-box;
	}
</style>
